/**
 * TogglePill — shared visual + a11y contract for a small stateful toggle
 * control that must *read* as clickable: label/value text, an optional
 * state-aware leading glyph (checkbox, cycle arrows, …), a hover-ring +
 * `cursor-pointer` affordance, `aria-pressed` reflecting the boolean state,
 * and a MANDATORY tooltip explaining what the states mean. A disabled pill
 * carries its OWN reason via `disabledTooltip` instead of the state-explainer
 * `tooltip` — the project's disabled-tooltip rule (see `RemoveButton` /
 * `AddButton`): a disabled control never goes mute.
 *
 * Closes SEL-13 / SEL-19 / COM-29 / ANA-29 (state-only micro-controls that
 * didn't read as clickable) — Advisor 2 §B3.
 *
 * Consumers: `SelectList`'s DISTINCT badge, `OrderByItemRow`'s sort-direction
 * badge. `LimitValueInline` (inline text edit) and `JoinLine`'s kind badge
 * (multi-value dropdown) match this component's VISUAL language directly —
 * their interaction models don't fit a boolean on/off toggle, so they don't
 * mount this component.
 *
 * Always self-provides its `VqeTooltip` (mirrors `RemoveButton`/`AddButton`)
 * so it renders standalone in unit tests without an app-root TooltipProvider.
 *
 * PRD: Ralph/VqePolish — Wave 0 primitive #7.
 */

import type { ReactNode } from 'react';

import { cn } from '../../../../../lib/utils';
import { VqeTooltip } from './VqeTooltip';

type TogglePillCommonProps = {
  /** Stable test id for the underlying button element. */
  readonly testId: string;
  /** Accessible name — should name the control, not just repeat the value. */
  readonly ariaLabel: string;
  /** Reflected as `aria-pressed`; also selects the active/inactive styling. */
  readonly pressed: boolean;
  /** The pill's label/value content (e.g. "DISTINCT", "DESC"). */
  readonly children: ReactNode;
  /** Optional leading glyph — pass a state-aware icon (checkbox, arrows, …). */
  readonly icon?: ReactNode;
  /** Mandatory — shown while enabled, must explain what the states mean. */
  readonly tooltip: ReactNode;
  readonly onClick?: () => void;
  /** Extra classes merged onto the button (rare per-site tweak). */
  readonly className?: string;
};

/**
 * Disabled/`disabledTooltip` are a discriminated union rather than two
 * independent optional props: a disabled pill can NOT omit its reason. This
 * makes the project's standing disabled-tooltip rule ("every disabled
 * control explains WHY") a compile-time contract for this primitive instead
 * of a convention callers can silently forget.
 */
export type TogglePillProps = TogglePillCommonProps &
  ({ readonly disabled?: false } | { readonly disabled: true; readonly disabledTooltip: ReactNode });

const BASE =
  'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ' +
  'uppercase tracking-wide transition-colors cursor-pointer hover:ring-1 hover:ring-ring/50 ' +
  'disabled:cursor-not-allowed disabled:hover:ring-0 disabled:opacity-70';

const ACTIVE = 'bg-primary/10 text-primary hover:bg-primary/20';
// The off state keeps a readable label (not `text-muted-foreground`, which is
// faint on the dark card background) — the dashed border carries "off".
const INACTIVE =
  'border border-dashed border-border text-foreground/80 hover:bg-accent hover:text-foreground';

/**
 * Shared hover-ring affordance — the part of TogglePill's visual language
 * that generalizes to controls whose interaction model does NOT fit the
 * boolean on/off contract here (an inline text edit, a multi-value
 * dropdown), so they don't mount the component itself but should still
 * *read* as clickable the same way. Current consumer: `LimitValueInline`'s
 * display button (Wave 0 primitive #7 — visual consistency only).
 */
export const TOGGLE_PILL_HOVER_AFFORDANCE = 'rounded transition-colors hover:ring-1 hover:ring-ring/50';

export function TogglePill(props: TogglePillProps) {
  const { testId, ariaLabel, pressed, children, icon, tooltip, onClick, className } = props;
  // Narrow on the discriminant rather than destructuring `disabled`/
  // `disabledTooltip` directly — they don't share a type across the union's
  // two branches, so TS only allows access once `props.disabled` is checked.
  const disabled = props.disabled ?? false;
  const disabledTooltip = props.disabled ? props.disabledTooltip : undefined;
  return (
    <VqeTooltip content={disabled ? disabledTooltip : tooltip} selfProvider>
      <button
        type="button"
        data-testid={testId}
        aria-label={ariaLabel}
        aria-pressed={pressed}
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        className={cn(BASE, pressed ? ACTIVE : INACTIVE, className)}
      >
        {icon}
        {children}
      </button>
    </VqeTooltip>
  );
}
